import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { FileExtension } from 'src/constants/fileExtensions';
import { useCreateProjectState } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import { defaultProjectFileUploadOptions } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { useProjectMultipleFileUploadsParser } from 'src/utils/hooks/useProjectFileUploadParser';

type UseFileUploadStateArgs = {
  setIsAddDataDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerFullSize: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerTitle: React.Dispatch<React.SetStateAction<string>>;
};

const getFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const useFileUploadState = ({
  setIsAddDataDrawerOpen,
  setDrawerFullSize,
  setDrawerTitle,
}: UseFileUploadStateArgs) => {
  const {
    fileMetadata,
    selectedFiles,
    setSelectedFiles,
    setFileContents,
    setFileMetadata,
  } = useCreateProjectState();

  const [previewFileIndex, setPreviewFileIndex] = React.useState<number | null>(
    null
  );

  const { fieldsByFileIdx, isParsing, isPartialByFileIdx, rowsByFileIdx } =
    useProjectMultipleFileUploadsParser(
      selectedFiles,
      defaultProjectFileUploadOptions
    );

  // Sync file metadata when parsing completes
  React.useEffect(() => {
    if (isParsing) {
      return;
    }

    setFileContents((prevFileContents) => {
      const newFileContents: Record<number, string> = {};

      let hasChanges = false;
      Object.keys(fieldsByFileIdx).forEach((idx) => {
        const index = Number(idx);
        // Ignore stale parser entries for files that are no longer selected.
        if (index >= selectedFiles.length) {
          return;
        }
        const newFields = fieldsByFileIdx[index] || [];
        const newRows = rowsByFileIdx[index] || [];
        const prevMetadata = fileMetadata[index];

        const contents = unparseCsv({
          fields: newFields.map(({ value }) => value),
          rows: newRows,
          fileOptions:
            prevMetadata?.fileOptions || defaultProjectFileUploadOptions,
        });
        if (contents !== prevFileContents[index]) {
          hasChanges = true;
        }
        newFileContents[index] = contents;
      });

      // Drop contents for files that are no longer selected (e.g. removed).
      if (
        Object.keys(newFileContents).length !==
        Object.keys(prevFileContents).length
      ) {
        hasChanges = true;
      }

      return hasChanges ? newFileContents : prevFileContents;
    });
    setFileMetadata((prevMetadata) => {
      const metadata: Record<number, FileCreationMetadata> = {
        ...prevMetadata,
      };
      let hasChanges = false;

      Object.keys(fieldsByFileIdx).forEach((idx) => {
        const index = Number(idx);
        // Ignore stale parser entries for files that are no longer selected.
        if (index >= selectedFiles.length) {
          return;
        }
        const newFields = fieldsByFileIdx[index] || [];
        const newRowCount = (rowsByFileIdx[index] || []).length;

        if (
          prevMetadata[index]?.fields !== newFields ||
          prevMetadata[index]?.rowCount !== newRowCount ||
          !prevMetadata[index]?.fileOptions
        ) {
          hasChanges = true;
          metadata[index] = {
            ...prevMetadata[index],
            fields: newFields,
            isPartial: isPartialByFileIdx[index],
            rowCount: newRowCount,
            fileOptions:
              prevMetadata[index]?.fileOptions ||
              defaultProjectFileUploadOptions,
            fileName:
              prevMetadata[index]?.fileName ||
              selectedFiles[index]?.name.replace(/\.csv$/i, '') ||
              '',
          };
        }
      });

      // Drop metadata for files that are no longer selected (e.g. removed) so a
      // later file added at the same index can't inherit the stale data.
      Object.keys(metadata).forEach((idx) => {
        const index = Number(idx);
        if (index >= selectedFiles.length) {
          delete metadata[index];
          hasChanges = true;
        }
      });

      return hasChanges ? metadata : prevMetadata;
    });
  }, [
    fieldsByFileIdx,
    isParsing,
    rowsByFileIdx,
    setFileMetadata,
    selectedFiles,
    isPartialByFileIdx,
    setFileContents,
    fileMetadata,
  ]);

  const handleDropAccepted = React.useCallback(
    (files: File[]) => {
      setSelectedFiles((prev) => {
        const newFiles = files.filter((newFile) => {
          return !prev.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size &&
              existingFile.lastModified === newFile.lastModified
          );
        });
        return [...prev, ...newFiles];
      });
    },
    [setSelectedFiles]
  );

  const mainDropzone = useDropzone({
    accept: FileExtension.CSV,
    multiple: true,
    noClick: false,
    onDropAccepted: handleDropAccepted,
  });

  const addDrawerDropzone = useDropzone({
    accept: FileExtension.CSV,
    multiple: true,
    noClick: false,
    onDropAccepted: (files: File[]) => {
      handleDropAccepted(files);
      requestAnimationFrame(() => {
        setIsAddDataDrawerOpen(false);
      });
    },
  });

  const handleRemoveFile = React.useCallback(
    (index: number) => {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

      setFileMetadata((prevMetadata) => {
        const reindexedMetadata: Record<number, FileCreationMetadata> = {};

        Object.entries(prevMetadata).forEach(([key, value]) => {
          const oldIndex = Number(key);
          if (oldIndex < index) {
            reindexedMetadata[oldIndex] = value;
          } else if (oldIndex > index) {
            reindexedMetadata[oldIndex - 1] = value;
          }
        });

        return reindexedMetadata;
      });
    },
    [setSelectedFiles, setFileMetadata]
  );

  const handleViewPreview = React.useCallback(
    (index: number) => {
      setPreviewFileIndex(index);
      setDrawerFullSize(true);
      setDrawerTitle(
        copy.components.projectResourceDialog.fileUploadPanel.title
      );
    },
    [setDrawerFullSize, setDrawerTitle]
  );

  const handleClosePreview = React.useCallback(() => {
    setPreviewFileIndex(null);
    setDrawerFullSize(false);
  }, [setDrawerFullSize]);

  const clearFiles = React.useCallback(() => {
    setSelectedFiles([]);
    setFileMetadata({});
  }, [setSelectedFiles, setFileMetadata]);

  return {
    previewFileIndex,
    mainDropzone,
    addDrawerDropzone,
    getFileKey,
    handleRemoveFile,
    handleViewPreview,
    handleClosePreview,
    clearFiles,
  };
};
